import { Box, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';

/**
 * App Selector component for switching between different apps within ALPS
 * @param {boolean} isCollapsed - Whether the sidebar is collapsed
 */
const AppSelector = ({ isCollapsed, user }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { tenantId } = useParams();
	const location = useLocation();

	// Determine current app based on URL
	const currentApp = location.pathname.includes('/apps/investing')
		? 'investing'
		: 'hub';

	// Only show Investing if user is Altvia power/admin user & env var is true
	const showInvesting =
		process.env.REACT_APP_SHOW_INVESTING === 'true' &&
		(user.role === 'altvia_admin' || user.role === 'altvia_power_user');

	// If not showing Investing, hide the selector entirely
	if (!showInvesting) {
		return null;
	}

	// List of available apps
	const apps = [
		{ id: 'hub', name: 'Hub' },
		{ id: 'investing', name: 'Investing' },
	];

	const handleAppChange = (event) => {
		const newApp = event.target.value;

		if (newApp !== null) {
			// Navigate to the selected app
			if (newApp === 'hub') {
				navigate(`/tenants/${tenantId}/marketplace`);
			} else {
				navigate(`/tenants/${tenantId}/apps/${newApp}`);
			}
		}
	};

	// Compact version for collapsed sidebar
	if (isCollapsed) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
				<Select
					value={currentApp}
					onChange={handleAppChange}
					size='small'
					sx={{
						minWidth: '40px',
						bgcolor: theme.palette.background.paper,
						color: theme.palette.text.primary,
						'& .MuiSelect-select': {
							padding: '5px',
							display: 'flex',
							justifyContent: 'center',
							bgcolor: theme.palette.background.paper,
							color: theme.palette.text.primary,
						},
					}}
				>
					{apps.map((app) => (
						<MenuItem
							key={app.id}
							value={app.id}
							sx={{
								bgcolor: theme.palette.background.paper,
								color: theme.palette.text.primary,
							}}
						>
							{app.id.charAt(0).toUpperCase()}
						</MenuItem>
					))}
				</Select>
			</Box>
		);
	}

	// Full sidebar view with labels
	return (
		<Box sx={{ px: 2, py: 1 }}>
			<Typography
				variant='subtitle2'
				sx={{ mb: 1, fontWeight: 'bold', color: theme.palette.text.secondary }}
			>
				Applications
			</Typography>
			<FormControl fullWidth size='small'>
				<Select
					value={currentApp}
					onChange={handleAppChange}
					displayEmpty
					sx={{
						bgcolor: theme.palette.background.paper,
						color: theme.palette.text.primary,
					}}
				>
					{apps.map((app) => (
						<MenuItem
							key={app.id}
							value={app.id}
							sx={{
								bgcolor: theme.palette.background.paper,
								color: theme.palette.text.primary,
							}}
						>
							{app.name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	);
};

export default AppSelector;
